/**
 * Workspace projects list component for admin workspace detail page.
 * Uses KiiList for consistent table layout with template column.
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminWorkspaceProjects } from "../../hooks/useAdminWorkspaces";
import { useWorkspaceProjectTemplates } from "../../hooks/useProjectTemplates";
import { useDateFormatter } from "../../hooks/useDateFormatter";
import { FolderKanban } from "lucide-react";
import { KiiList, type KiiListColumn } from "@/components/kii";
import { TableRow, TableCell } from "@/components/ui/table";
import type { Project } from "../../services/naraDoor";

interface WorkspaceProjectsListProps {
  workspaceId: string;
}

const columns: KiiListColumn[] = [
  { key: "name", label: "Name" },
  { key: "description", label: "Description" },
  { key: "template", label: "Template" },
  { key: "documents", label: "Documents" },
  { key: "created", label: "Created" },
];

export function WorkspaceProjectsList({ workspaceId }: WorkspaceProjectsListProps) {
  const navigate = useNavigate();
  const { formatDate } = useDateFormatter();
  const { data, isLoading, error } = useAdminWorkspaceProjects(workspaceId);
  const { data: templatesData } = useWorkspaceProjectTemplates(workspaceId);

  const projects = data?.projects || [];

  const templateMap = useMemo(() => {
    const map = new Map<string, string>();
    if (templatesData?.templates) {
      for (const t of templatesData.templates) {
        map.set(t.id, t.name);
      }
    }
    return map;
  }, [templatesData]);

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-sm text-red-800 dark:text-red-200">{error.message}</p>
      </div>
    );
  }

  return (
    <KiiList<Project>
      columns={columns}
      data={projects}
      isLoading={isLoading}
      emptyMessage="No projects in this workspace"
      emptyIcon={<FolderKanban className="h-10 w-10 text-muted-foreground" />}
      showCount={true}
      countLabel={(count) => `${count} project${count !== 1 ? "s" : ""}`}
      onRowClick={(project) => navigate(`/admin/workspaces/${workspaceId}/projects/${project.id}`)}
      renderRow={(project) => (
        <TableRow key={project.id}>
          <TableCell>
            <div className="flex items-center gap-3">
              <FolderKanban className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-medium">{project.name}</span>
            </div>
          </TableCell>
          <TableCell className="text-muted-foreground max-w-xs truncate">
            {project.description || "—"}
          </TableCell>
          <TableCell className="text-muted-foreground">
            {project.template_id ? templateMap.get(project.template_id) || "—" : "—"}
          </TableCell>
          <TableCell className="text-muted-foreground">
            {project.document_count ?? 0}
          </TableCell>
          <TableCell className="text-muted-foreground">
            {formatDate(project.created_at)}
          </TableCell>
        </TableRow>
      )}
    />
  );
}
